import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import applySessionEvent, { eventIsStale, eventRevealsMissedState } from '../applySessionEvent';
import { sprekersApi } from '../api';
import { resolveIsOverviewAndTarget } from '../canvas/canvasSelectors';
import CanvasStepSummary from '../canvas/CanvasStepSummary';
import useLiveSessionState from '../hooks/useLiveSessionState';
import useLiveSessionStream from '../hooks/useLiveSessionStream';
import { persistLiveSession, readLiveSession } from '../liveSessionStorage';
import { computeLiveDisplayStatus, LIVE_DISPLAY_STATUS_LABELS } from '../liveStatus';
import useSpeakerProfiles from '../hooks/useSpeakerProfiles';
import CanvasPresentationControls from '../components/CanvasPresentationControls';
import CurrentSlidePreview from '../components/CurrentSlidePreview';
import NextSlidePreview from '../components/NextSlidePreview';
import PresentationControls from '../components/PresentationControls';
import SpeakerNotesPanel from '../components/SpeakerNotesPanel';
import { useAuth } from '@/context/AuthContext';
import type { CanvasBoardDetail, PresentationDetail, PresentationSession, SpeakingEngagement } from '../types';

function formatSeconds(totalSeconds: number): string {
  const sign = totalSeconds < 0 ? '-' : '';
  const abs = Math.abs(Math.round(totalSeconds));
  const minutes = Math.floor(abs / 60);
  const seconds = abs % 60;
  return `${sign}${minutes}:${String(seconds).padStart(2, '0')}`;
}

/**
 * Bediening van de live sessie (sectie 16.1): telefoon van de spreker na het
 * scannen van de QR-code, of een tweede tabblad op dezelfde laptop als
 * handmatige terugval voor het presentatieteam (sectie 17.1) -- die laatste
 * gebruikt de sessie die al in sessionStorage staat, zonder opnieuw te
 * hoeven koppelen. Bediening en sprekersweergave (privénotities/preview)
 * blijven bewust op één mobiel scherm (geen kunstmatige routesplitsing),
 * maar wel duidelijk gescheiden in eigen componenten: PresentationControls,
 * SpeakerNotesPanel, CurrentSlidePreview, NextSlidePreview.
 */
export default function LiveControlPage() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const location = useLocation();
  const { user, hasRole } = useAuth();
  const profiles = useSpeakerProfiles();

  const { session, sessionRef, updateSession } = useLiveSessionState(() => {
    const fromNav = (location.state as { session?: PresentationSession } | null)?.session;
    return fromNav || (sessionId ? readLiveSession(sessionId) : null) || null;
  });
  const [engagement, setEngagement] = useState<SpeakingEngagement | null>(null);
  const [presentation, setPresentation] = useState<PresentationDetail | null>(null);
  const [canvasBoard, setCanvasBoard] = useState<CanvasBoardDetail | null>(null);
  const [isOverview, setIsOverview] = useState(false);
  const [busy, setBusy] = useState(false);
  const [retryKey, setRetryKey] = useState(0);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (session) persistLiveSession(session);
  }, [session]);

  useEffect(() => {
    if (!session) return;
    const engagementId = session.speaking_engagement_id;
    sprekersApi.getEngagement(engagementId).then(setEngagement).catch(() => setEngagement(null));
    sprekersApi.getPresentation(engagementId).then(setPresentation).catch(() => setPresentation(null));
  }, [session?.speaking_engagement_id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!presentation || presentation.presentation_type !== 'canvas') {
      setCanvasBoard(null);
      return;
    }
    sprekersApi.getCanvasBoard(presentation.id).then(setCanvasBoard).catch(() => setCanvasBoard(null));
  }, [presentation]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Alleen initialiseren zodra het board binnenkomt (vóór het eerste
    // SSE-event) -- latere presentation.focus_changed-events werken
    // isOverview zelf bij via de is_overview-vlag uit het event.
    if (!canvasBoard || !session) return;
    setIsOverview(
      resolveIsOverviewAndTarget(canvasBoard.path, session.current_path_index, session.canvas_manual_overview).isOverview,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canvasBoard]);

  useEffect(() => {
    const interval = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(interval);
  }, []);

  const streamStatus = useLiveSessionStream(session?.status === 'beeindigd' || session?.status === 'verlopen' ? null : session?.id, (event) => {
    // Een snapshot (bij elke (her)verbinding) of delta die ouder is dan onze
    // toestand mag ook de overzichtsvlag niet terugzetten.
    const stale = eventIsStale(sessionRef.current, event);
    updateSession((prev) => (prev ? applySessionEvent(prev, event) : prev));
    if (
      !stale
      && (event.type === 'presentation.focus_changed' || event.type === 'presentation.state')
      && typeof event.is_overview === 'boolean'
    ) {
      setIsOverview(event.is_overview);
    }
  }, {
    needsResync: (event) => eventRevealsMissedState(sessionRef.current, event),
    retryKey,
  });

  const displayStatus = session ? computeLiveDisplayStatus(session, streamStatus) : 'onherstelbaar';

  const slides = useMemo(() => presentation?.slides || [], [presentation]);
  const currentIndex = useMemo(
    () => slides.findIndex((slide) => slide.id === session?.current_slide_id),
    [slides, session?.current_slide_id],
  );
  const currentSlide = currentIndex >= 0 ? slides[currentIndex] : null;
  const nextSlide = currentIndex >= 0 && currentIndex + 1 < slides.length ? slides[currentIndex + 1] : null;

  const ownSpeakerProfileId = useMemo(
    () => profiles.find((p) => p.user_id === user?.id)?.id,
    [profiles, user?.id],
  );
  const isInvitedSpeaker = !!engagement && !!ownSpeakerProfileId && (
    engagement.speaker_id === ownSpeakerProfileId || engagement.co_speaker_ids.includes(ownSpeakerProfileId)
  );
  const isEligibleForNotes = isInvitedSpeaker || hasRole('admin');

  const elapsedSeconds = session ? (now - new Date(session.started_at).getTime()) / 1000 : 0;
  const targetSeconds = engagement?.target_duration_minutes ? engagement.target_duration_minutes * 60 : null;
  const remainingSeconds = targetSeconds !== null ? targetSeconds - elapsedSeconds : null;

  const runAction = async (action: () => Promise<PresentationSession>) => {
    if (busy) return;
    setBusy(true);
    try {
      const updated = await action();
      // Een trage HTTP-respons mag een intussen via SSE binnengekomen,
      // nieuwere toestand niet terugzetten.
      updateSession((prev) => (
        prev && (updated.state_version ?? 0) < (prev.state_version ?? 0) ? prev : updated
      ));
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Actie mislukt');
    } finally {
      setBusy(false);
    }
  };

  const handleEnd = () => {
    if (!session) return;
    if (!window.confirm('Weet je zeker dat je de live sessie wilt beëindigen?')) return;
    runAction(() => sprekersApi.endSession(session.id));
  };

  if (!session || !sessionId) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-slate-950 p-6 text-center text-slate-300">
        <p>Geen sessiegegevens gevonden op dit apparaat.</p>
        <p className="text-sm text-slate-500">Scan de QR-code op het beamerscherm opnieuw om te koppelen.</p>
      </div>
    );
  }

  if (displayStatus === 'beeindigd' || displayStatus === 'verlopen' || displayStatus === 'onherstelbaar') {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-slate-950 p-6 text-center text-white">
        <p className="text-lg">{LIVE_DISPLAY_STATUS_LABELS[displayStatus]}</p>
        <Link to={`/sprekers/${session.speaking_engagement_id}`} className="text-sm text-teal-300 underline">
          Terug naar spreekbeurt
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <div className="mx-auto flex max-w-md flex-col gap-4 p-4">
        <div className="flex items-center justify-between text-xs text-slate-400">
          <span>Verstreken {formatSeconds(elapsedSeconds)}</span>
          {remainingSeconds !== null && <span>Resterend {formatSeconds(remainingSeconds)}</span>}
          {slides.length > 0 && <span>Slide {Math.max(currentIndex + 1, 0)}/{slides.length}</span>}
        </div>
        {slides.length > 0 && (
          <div className="h-1 w-full rounded-full bg-slate-800">
            <div
              className="h-1 rounded-full bg-teal-400 transition-all"
              style={{ width: `${((Math.max(currentIndex, 0) + 1) / slides.length) * 100}%` }}
            />
          </div>
        )}
        {(displayStatus === 'verbroken' || displayStatus === 'verbroken_gestopt') && (
          <div className="flex items-center gap-2 rounded-md bg-amber-400/10 px-3 py-1.5 text-xs text-amber-300">
            <span className="h-2 w-2 animate-pulse rounded-full bg-amber-400" />
            {LIVE_DISPLAY_STATUS_LABELS[displayStatus]}
            {displayStatus === 'verbroken_gestopt' && (
              <button
                type="button" onClick={() => setRetryKey((key) => key + 1)}
                className="ml-auto rounded-md bg-amber-300 px-2 py-0.5 font-medium text-black hover:bg-amber-200"
              >
                Opnieuw verbinden
              </button>
            )}
          </div>
        )}

        {presentation?.presentation_type === 'canvas' ? (
          <>
            <CanvasStepSummary
              frame={canvasBoard?.frames.find((frame) => frame.id === session.current_item_id) || null}
              isOverview={isOverview}
              pathIndex={session.current_path_index}
              pathLength={canvasBoard?.path.length || 0}
            />
            <CanvasPresentationControls
              busy={busy}
              canGoPrevious={session.current_path_index > 0}
              canGoNext={!canvasBoard || session.current_path_index < canvasBoard.path.length - 1}
              isOverview={isOverview}
              isBlackout={session.is_blackout}
              onPrevious={() => runAction(() => sprekersApi.previousStep(session.id))}
              onNext={() => runAction(() => sprekersApi.nextStep(session.id))}
              onToggleOverview={() => runAction(() => sprekersApi.toggleCanvasOverview(session.id))}
              onGoToTheme={() => runAction(() => sprekersApi.gotoTheme(session.id))}
              onToggleBlackout={() => runAction(() => sprekersApi.toggleBlackout(session.id))}
              onEnd={handleEnd}
            />
          </>
        ) : (
          <>
            <CurrentSlidePreview slide={currentSlide} blackout={session.is_blackout} />
            <NextSlidePreview slide={nextSlide} />

            <PresentationControls
              busy={busy}
              canGoPrevious={currentIndex > 0}
              canGoNext={slides.length === 0 || currentIndex < slides.length - 1}
              isBlackout={session.is_blackout}
              onPrevious={() => runAction(() => sprekersApi.previousSlide(session.id))}
              onNext={() => runAction(() => sprekersApi.nextSlide(session.id))}
              onToggleBlackout={() => runAction(() => sprekersApi.toggleBlackout(session.id))}
              onEnd={handleEnd}
            />
          </>
        )}

        <SpeakerNotesPanel engagementId={session.speaking_engagement_id} eligible={isEligibleForNotes} />
      </div>
    </div>
  );
}
